import { FreeSpinsInfoEvent, SpinResponse, SpecialSymbolGroupType } from '@cgs/data-access';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IGameStateMachineProvider,
  ResourcesComponent,
  RESOURCES_COMPONENT_TOKEN,
} from '@cgs/machines';
import { Container, EmptyAction, FunctionAction, IntervalAction, TextSceneObject } from '@cgs/wge';
import { FreeSpinEventType } from './corsica-free-spins.types';

const enum SceneNames {
  TotalText = 'fs_total_score',
}

export const CORSICA_FREE_SPINS_TOTAL_COMPONENT_TOKEN = Symbol('CorsicaFreeSpinsTotalComponent');

export class CorsicaFreeSpinsTotalComponent {
  private readonly gameStateMachine: GameStateMachine<SpinResponse>;
  private readonly totalFreeSpins: TextSceneObject[];
  private totalMultiplier: number = 0;

  constructor(container: Container) {
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.totalFreeSpins = container
      .resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN)
      .slot.findAllById(SceneNames.TotalText)!;

    this.gameStateMachine.beginFreeSpins.appendLazyAnimation(() => this.updateStartTotal());
    this.gameStateMachine.animation.appendLazyAnimation(() => this.updateTotal());
    this.gameStateMachine.accelerate.appendLazyAnimation(() => this.updateTotalAfter());
    this.gameStateMachine.freeSpinsRecovery.appendLazyAnimation(() => this.updateTotal());
  }

  public addToTotal(multiplier: number): void {
    this.totalMultiplier += multiplier;
    this.totalFreeSpins.forEach((text) => (text.text = `${this.totalMultiplier}`));
  }

  private updateTotalAfter(): IntervalAction {
    if (!this.gameStateMachine.curResponse.isFreeSpins) {
      return new EmptyAction();
    }

    const multiplier =
      this.gameStateMachine.curResponse.freeSpinsInfo?.currentFreeSpinsGroup?.multiplier;

    if (!multiplier) {
      return new EmptyAction();
    }

    this.totalMultiplier = multiplier;

    return new FunctionAction(() => {
      this.totalFreeSpins.forEach((text) => (text.text = `${this.totalMultiplier}`));
    });
  }

  private updateTotal(): IntervalAction {
    if (!this.gameStateMachine.curResponse.isFreeSpins) {
      return new EmptyAction();
    }
    const multiplier = (this.gameStateMachine.curResponse.specialSymbolGroups || [])
      .filter(
        (group) =>
          group.type === SpecialSymbolGroupType.Multiplier &&
          group.subType === FreeSpinEventType.Init
      )
      .reduce((acc, group) => acc + group.totalWin!, 0);

    if (!multiplier) {
      return new EmptyAction();
    }

    this.totalMultiplier = multiplier;

    return new FunctionAction(() => {
      this.totalFreeSpins.forEach((text) => (text.text = `${multiplier}`));
    });
  }

  private updateStartTotal(): IntervalAction {
    if (
      this.gameStateMachine.curResponse.freeSpinsInfo?.event === FreeSpinsInfoEvent.FreeSpinsAdded
    ) {
      return new EmptyAction();
    }

    const specialSymbolGroups = this.gameStateMachine.curResponse.specialSymbolGroups || [];

    const multiplier = specialSymbolGroups
      .filter(
        (group) =>
          group.type === SpecialSymbolGroupType.Multiplier &&
          group.subType === FreeSpinEventType.Init
      )
      .reduce((acc, group) => acc + group.totalWin!, 0);

    if (!multiplier) {
      return new EmptyAction();
    }

    this.totalMultiplier = multiplier;

    return new FunctionAction(() => {
      this.totalFreeSpins.forEach((text) => (text.text = `${multiplier}`));
    });
  }
}
